import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NotificationContext = createContext(null);

// ── Allowed parent origins (same as AuthContext TRUSTED_ORIGINS) ──
const TRUSTED_ORIGINS = [
  'https://localhost:5001',
  'https://localhost:44381',
  'https://localhost:7109',
  'http://localhost:3000',
  'https://itpn-riskassessment-staging.azurewebsites.net',
  'https://itpn-riskassessment.azurewebsites.net',
  'https://myriskshield.com',
  window.location.origin,
];

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // ── Listen for RISKWEBAPP_NOTIFICATIONS messages from the parent iframe host ──
  useEffect(() => {
    const handleMessage = (event) => {
      if (!TRUSTED_ORIGINS.includes(event.origin) && event.origin !== '*') return;

      const msg = event.data;
      if (!msg || msg.type !== 'RISKWEBAPP_NOTIFICATIONS') return;

      const { items = [], unreadCount: count = 0 } = msg.payload || {};
      setNotifications(items);
      setUnreadCount(count);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // ── Tell the parent host to close its notification dropdown when the user ──
  // clicks anywhere inside this embedded app. The parent's own click-outside
  // handler never fires for clicks that happen inside the iframe, so we relay
  // a close signal to it via postMessage.
  useEffect(() => {
    const notifyParentClose = () => {
      try {
        window.parent.postMessage({ type: 'TRUSTCENTER_CLOSE_NOTIFICATIONS' }, '*');
      } catch (e) {
        console.warn('[NotificationContext] close-notifications relay failed:', e);
      }
    };

    document.addEventListener('mousedown', notifyParentClose);
    return () => document.removeEventListener('mousedown', notifyParentClose);
  }, []);

  // ── Mark a notification as read (optimistic local update) ──
  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // ── Publish a notification to the parent RiskWebApp via postMessage ──
  // The parent TrustCenter.razor script listens, POSTs to /Notifications/Add,
  // then the polling loop sends it back here.
  const publishNotification = useCallback((notification) => {
    try {
      window.parent.postMessage(
        { type: 'TRUSTCENTER_NOTIFICATION', payload: notification },
        '*'
      );
    } catch (e) {
      console.warn('[NotificationContext] publishNotification failed:', e);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, publishNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};
